import { Close, Save } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Fade, FormControl, Grid, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useState } from 'react';
import { Button as SMButton, Modal, StatesDropdown } from '../../../components';
import { postCounty, putCounty } from '../../../fetch';
import { ICounty } from '../../../models/counties';

interface IManageCountiesModal {
  open: boolean;
  onClose: () => void;
  county: ICounty;
  fetchCounties: () => Promise<void>;
}

export const ManageCountiesModal: FC<IManageCountiesModal> = ({
  open,
  onClose,
  county,
  fetchCounties
}) => {
  const handleManageCountiesClose = () => {
    // reset state
    setSelectedState('');
    setCountyName('');
    setTaxRate('');

    onClose();
  };
  const { enqueueSnackbar } = useSnackbar();
  const [selectedState, setSelectedState] = useState('');
  const [countyName, setCountyName] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [isSaving, setSaving] = useState(false);

  useEffect(() => {
    if (county) {
      setSelectedState(county.state);
      setCountyName(county.name);
      setTaxRate(county.taxRate.toFixed(2));
    }
  }, [county]);

  const updateCounty = async (updateableCounty: ICounty) => {
    try {
      setSaving(true);
      await putCounty(updateableCounty.state, updateableCounty.name, updateableCounty.taxRate);

      await fetchCounties();
      handleManageCountiesClose();
      enqueueSnackbar('County has been updated', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `There was a problem updating the county`, {
        variant: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const createCounty = async (createableCounty: ICounty) => {
    try {
      setSaving(true);
      await postCounty(createableCounty.state, createableCounty.name, createableCounty.taxRate);
      await fetchCounties();
      handleManageCountiesClose();
      enqueueSnackbar('County has been created!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `There was a problem creating the county`, {
        variant: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleManageCountiesClose}
        maxWidth="sm"
        title={county ? `Manage ${county.name} County` : 'Add County'}
      >
        <Fade in={open}>
          <div>
            <Box marginTop={1}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <StatesDropdown
                    handleChange={val => setSelectedState(val)}
                    value={selectedState || 'Loading...'}
                    isRequired
                    isDisabled={!!county}
                    isAdminPortal={true}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      disabled={!!county}
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="countyName"
                      autoComplete="none"
                      label="County Name"
                      value={countyName}
                      onBlur={e => {}}
                      onChange={e => {
                        setCountyName(e.target.value);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="taxRate"
                      autoComplete="none"
                      label="Tax Rate"
                      value={taxRate.toString()}
                      onChange={e => {
                        const re = /^\d{0,10}\.?\d{0,2}$/;
                        const value = e.target.value;
                        // make sure it is number only before we update state
                        if (value === '' || re.test(value)) {
                          setTaxRate(value);
                        }
                      }}
                      error={
                        taxRate !== '' &&
                        (taxRate !== parseFloat(taxRate).toFixed(2) ||
                          parseFloat(taxRate) < 0 ||
                          parseFloat(taxRate) >= 100)
                      }
                      helperText={
                        taxRate !== ''
                          ? taxRate !== parseFloat(taxRate).toFixed(2)
                            ? 'Please use two decimal places'
                            : parseFloat(taxRate) < 0
                            ? 'Tax Rate must be greater than 0'
                            : parseFloat(taxRate) >= 100
                            ? 'Tax Rate must be less than 100'
                            : ''
                          : ''
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box marginTop="1rem">
                    <ButtonGroup>
                      <SMButton
                        id="save"
                        color="primary"
                        variant="contained"
                        disabled={
                          taxRate === '' ||
                          taxRate !== parseFloat(taxRate).toFixed(2) ||
                          parseFloat(taxRate) < 0 ||
                          parseFloat(taxRate) >= 100 ||
                          !countyName ||
                          !selectedState
                        }
                        loading={isSaving ? 'Saving...' : ''}
                        startIcon={<Save />}
                        onClick={async () => {
                          setSaving(true);
                          if (county) {
                            await updateCounty({
                              state: county.state,
                              name: county.name,
                              taxRate: parseFloat(taxRate)
                            });
                          } else {
                            await createCounty({
                              state: selectedState,
                              name: countyName,
                              taxRate: parseFloat(taxRate)
                            });
                          }
                        }}
                      >
                        Save
                      </SMButton>
                      <Button
                        id="cancel"
                        variant="contained"
                        color="inherit"
                        startIcon={<Close />}
                        onClick={() => {
                          handleManageCountiesClose();
                        }}
                      >
                        Cancel
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </div>
        </Fade>
      </Modal>
    </>
  );
};
